/**
 * Periods — the single implementation of budget-cycle boundaries (INV-2).
 *
 * An expense stores only `occurred_at`. Which period it belongs to is computed
 * here, every time, from the user's `cycleStartDay` and `timezone`. Nothing is
 * denormalised onto the row, because the cut-off day is user-configurable and a
 * stored period label would silently misfile every historical expense the moment
 * it changed.
 *
 * A period is half-open: `[start, end)`. Boundaries are local midnight in the
 * USER's timezone — never the device's, never UTC. An expense logged at 23:58
 * belongs to the day it was 23:58 for them.
 *
 * **Do not reimplement any of this elsewhere.** A second copy of the boundary
 * rule that drifts by one day silently misfiles expenses and crashes nothing.
 */

export interface Period {
  /** Inclusive start — local midnight in the user's timezone. */
  readonly start: Date;
  /** Exclusive end — local midnight of the next period's first day. */
  readonly end: Date;
}

export const MIN_CYCLE_START_DAY = 1;
export const MAX_CYCLE_START_DAY = 31;

const MS_PER_DAY = 86_400_000;
const MS_PER_SECOND = 1_000;
const MINUTES_PER_HOUR = 60;

/** A calendar date with no time and no zone. */
export interface YMD {
  readonly year: number;
  /** 1-12. */
  readonly month: number;
  readonly day: number;
}

/**
 * A calendar date `delta` days from `ymd`. Exact for every delta.
 *
 * **Do not do this by adding milliseconds to an instant.** A local day is 23 or
 * 25 hours across a DST transition, so `start + 7 * 86_400_000` lands an hour
 * off the intended midnight — and every grain boundary in `grain.ts` is built by
 * stepping whole days from a period's first day. UTC has no such day, so the
 * arithmetic is done there and the zone is put back by `zonedMidnight()`.
 */
export function shiftDays(ymd: YMD, delta: number): YMD {
  if (!Number.isInteger(delta)) {
    throw new Error(`shiftDays moves whole days, received ${String(delta)}`);
  }
  const shifted = new Date(Date.UTC(ymd.year, ymd.month - 1, ymd.day) + delta * MS_PER_DAY);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/**
 * Formatters, cached by timezone.
 *
 * Constructing an `Intl.DateTimeFormat` is the single most expensive thing in
 * this module — ~0.1ms each, and `periodBounds()` needs three, which made one
 * period boundary cost ~0.37ms. That is invisible for the current period and
 * ruinous the moment anything walks periods in a loop: the rollover carry and
 * the Ledger's period picker both do, so the picker was freezing the main
 * thread for seconds before this cache existed.
 *
 * Safe to share: a formatter is stateless for formatting, and the only input
 * that varies is the timezone — of which a user has exactly one.
 */
const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

function offsetFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = offsetFormatters.get(timeZone);
  if (cached) return cached;

  const created = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  offsetFormatters.set(timeZone, created);
  return created;
}

/**
 * Offset of `timeZone` from UTC, in ms, at the instant `date`.
 * Positive east of Greenwich (Asia/Singapore is +8h).
 */
function timeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = offsetFormatter(timeZone).formatToParts(date);

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Timezone ${timeZone} produced no ${type} part`);
    return Number.parseInt(part.value, 10);
  };

  const asIfUtc = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour") % 24,
    read("minute"),
    read("second"),
  );
  // Truncate to the second — formatToParts has no ms, and offsets are never sub-second.
  return asIfUtc - Math.floor(date.getTime() / MS_PER_SECOND) * MS_PER_SECOND;
}

/** Strict ISO instant expressed in the profile zone, including its explicit offset. */
export function isoWithOffset(date: Date, timeZone: string): string {
  const offset = timeZoneOffsetMs(date, timeZone);
  const offsetMinutes = Math.abs(offset) / (MS_PER_SECOND * MINUTES_PER_HOUR);
  const hours = String(Math.floor(offsetMinutes / MINUTES_PER_HOUR)).padStart(2, "0");
  const minutes = String(offsetMinutes % MINUTES_PER_HOUR).padStart(2, "0");
  return `${new Date(date.getTime() + offset).toISOString().slice(0, -1)}${offset < 0 ? "-" : "+"}${hours}:${minutes}`;
}

/**
 * The label formatters, cached on the same reasoning as `offsetFormatters`.
 *
 * Keyed on locale *and* timezone, because both vary the output — caching on
 * timezone alone would hand an `en-SG` caller a formatter built for another
 * locale, which is a wrong label rather than a slow one.
 */
const labelFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * A label formatter, built once per `key`.
 *
 * Exported so `grain.ts` labels its windows through the same cache rather than
 * standing up a second one — the cost this exists to avoid is per-formatter, not
 * per-module, and a strip renders up to 30 labels at a time.
 */
export function labelFormatter(key: string, build: () => Intl.DateTimeFormat): Intl.DateTimeFormat {
  const cached = labelFormatters.get(key);
  if (cached) return cached;
  const created = build();
  labelFormatters.set(key, created);
  return created;
}

/** The calendar date, in the user's timezone, of a given instant. */
export function localYMD(date: Date, timeZone: string): YMD {
  const offset = timeZoneOffsetMs(date, timeZone);
  const shifted = new Date(date.getTime() + offset);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/**
 * The instant of local midnight on a given calendar date in `timeZone`.
 *
 * Two correction passes: the offset must be sampled at the *result*, not at the
 * guess, or a DST transition between the two shifts the answer by an hour.
 * Singapore has no DST, but this must be correct for every user.
 */
export function zonedMidnight(ymd: YMD, timeZone: string): Date {
  const guess = Date.UTC(ymd.year, ymd.month - 1, ymd.day, 0, 0, 0);
  let instant = guess - timeZoneOffsetMs(new Date(guess), timeZone);
  instant = guess - timeZoneOffsetMs(new Date(instant), timeZone);
  return new Date(instant);
}

/** Days in a given 1-12 month. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** `YYYY-MM-DD`, the form an `<input type="date">` speaks. */
export function ymdToIso(ymd: YMD): string {
  return `${String(ymd.year).padStart(4, "0")}-${String(ymd.month).padStart(2, "0")}-${String(ymd.day).padStart(2, "0")}`;
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The narrowest and widest year a date field may carry.
 *
 * Not decoration: a hand-typed `0202` parses as a perfectly well-formed date,
 * and an expense filed under it makes the period picker walk eighteen centuries
 * of periods and throw at `MAX_PERIOD_WALK` (`period_nav.ts`). A year outside
 * this range is a typo rather than a date anyone means, so it is rejected at the
 * parse boundary instead of being carried into the ledger. The *usable* range is
 * far narrower still and belongs to the caller — the edit sheet allows the last
 * five years and nothing in the future.
 */
const MIN_YEAR = 1900;
const MAX_YEAR = 2999;

/**
 * Parse `YYYY-MM-DD`. Returns null on anything malformed or impossible.
 *
 * **Not `new Date(text)`.** That parses `2026-02-31` into 3 March without a
 * murmur, so a typo would silently file an expense in the wrong month — and it
 * reads a bare date string as UTC, which is a day out for every user east of
 * Greenwich. This validates the calendar date itself and leaves the zone to
 * `zonedMidnight`/`withLocalDate` (INV-7).
 *
 * The same regex appears privately in `period_nav.ts` and `grain.ts`; folding
 * those into this is a separate change, deliberately not made here.
 */
export function parseYmd(text: string): YMD | null {
  // Sliced rather than read off capture groups: the pattern has already proved
  // the shape, and fixed offsets need no undefined-checking to satisfy
  // `noUncheckedIndexedAccess`.
  if (!YMD_PATTERN.test(text)) return null;

  const year = Number.parseInt(text.slice(0, 4), 10);
  const month = Number.parseInt(text.slice(5, 7), 10);
  const day = Number.parseInt(text.slice(8, 10), 10);

  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > daysInMonth(year, month)) return null;

  return { year, month, day };
}

/**
 * The same wall-clock time of day, on a different local calendar date.
 *
 * Moving 20 Jul 09:14 to 18 Jul yields 18 Jul 09:14 **in the user's zone** — not
 * 18 Jul 00:00, which would refile every late-evening expense in a zone east of
 * UTC, and not a UTC-arithmetic result that lands on the 17th.
 *
 * The offset into the local day is measured against `zonedMidnight`, so a zone
 * whose offset differs between the two dates is handled by construction rather
 * than by adding milliseconds. Where a target day is *shorter* than the source
 * one (only possible across a DST transition), the result is **clamped** to one
 * millisecond before the next local midnight: an edit must never silently land
 * on a day other than the one the user tapped. Singapore has no DST; every other
 * primitive here is zone-generic and so is this.
 */
export function withLocalDate(instant: Date, ymd: YMD, timeZone: string): Date {
  if (!Number.isFinite(instant.getTime())) {
    // Loud, and specific (INV-7). Unguarded, this would reach
    // `Intl.DateTimeFormat.formatToParts`, whose RangeError says nothing about
    // whose date it was or what the caller should do instead. A caller holding
    // an unreadable instant has no time of day to preserve and wants
    // `zonedMidnight` — not a silently plausible-looking wrong answer.
    throw new Error(
      `withLocalDate needs a readable instant to take the time of day from, received "${String(instant)}"`,
    );
  }

  const sourceMidnight = zonedMidnight(localYMD(instant, timeZone), timeZone);
  const offsetIntoDay = instant.getTime() - sourceMidnight.getTime();

  const moved = new Date(zonedMidnight(ymd, timeZone).getTime() + offsetIntoDay);
  if (ymdToIso(localYMD(moved, timeZone)) === ymdToIso(ymd)) return moved;

  return new Date(zonedMidnight(shiftDays(ymd, 1), timeZone).getTime() - 1);
}

/**
 * The cycle start day actually used for a given month.
 *
 * Days 29-31 **clamp** to the last day of a short month — they never skip. A
 * `cycleStartDay` of 31 starts on 28 February in a non-leap year, so every day
 * of the year belongs to exactly one period and none belongs to two.
 */
export function clampedStartDay(year: number, month: number, cycleStartDay: number): number {
  return Math.min(cycleStartDay, daysInMonth(year, month));
}

export function assertValidCycleStartDay(day: number): void {
  if (!Number.isInteger(day) || day < MIN_CYCLE_START_DAY || day > MAX_CYCLE_START_DAY) {
    throw new Error(
      `cycleStartDay must be an integer ${String(MIN_CYCLE_START_DAY)}-${String(MAX_CYCLE_START_DAY)}, received ${String(day)}`,
    );
  }
}

function addMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const zero = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zero / 12), month: (zero % 12) + 1 };
}

/**
 * The period containing `date`, for a user with the given cut-off day and timezone.
 * **Every period question in the app routes through here.**
 */
export function periodBounds(date: Date, cycleStartDay: number, timeZone: string): Period {
  assertValidCycleStartDay(cycleStartDay);

  const today = localYMD(date, timeZone);
  const startDayThisMonth = clampedStartDay(today.year, today.month, cycleStartDay);

  const startMonth =
    today.day >= startDayThisMonth
      ? { year: today.year, month: today.month }
      : addMonth(today.year, today.month, -1);

  const endMonth = addMonth(startMonth.year, startMonth.month, 1);

  return {
    start: zonedMidnight(
      {
        year: startMonth.year,
        month: startMonth.month,
        day: clampedStartDay(startMonth.year, startMonth.month, cycleStartDay),
      },
      timeZone,
    ),
    end: zonedMidnight(
      {
        year: endMonth.year,
        month: endMonth.month,
        day: clampedStartDay(endMonth.year, endMonth.month, cycleStartDay),
      },
      timeZone,
    ),
  };
}

/**
 * The period's first day as `YYYY-MM-DD`, in the USER's timezone.
 *
 * **Use this, never `period.start.toISOString().slice(0, 10)`.** That shortcut
 * is wrong for every timezone east of UTC, including the default: the period
 * starting 1 Jul 2026 in Asia/Singapore begins at 2026-06-30T16:00Z, so slicing
 * the ISO string yields "2026-06-30". Budgets keyed off the wrong date silently
 * stop matching the budgets read back by `baseBudget` (INV-2).
 */
export function periodStartDate(period: Period, timeZone: string): string {
  return ymdToIso(localYMD(period.start, timeZone));
}

/** The period immediately before `period`. */
export function previousPeriod(
  period: Period,
  cycleStartDay: number,
  timeZone: string,
): Period {
  return periodBounds(new Date(period.start.getTime() - 1), cycleStartDay, timeZone);
}

/** The period immediately after `period`. */
export function nextPeriod(period: Period, cycleStartDay: number, timeZone: string): Period {
  return periodBounds(period.end, cycleStartDay, timeZone);
}

export function isInPeriod(date: Date, period: Period): boolean {
  const t = date.getTime();
  return t >= period.start.getTime() && t < period.end.getTime();
}

/** Whole days in the period. Varies — a 31st cut-off gives periods of 28 to 31 days. */
export function periodLengthDays(period: Period): number {
  return Math.round((period.end.getTime() - period.start.getTime()) / MS_PER_DAY);
}

/**
 * Whole **local calendar days** from `from` to `to`. Negative if `to` is earlier.
 *
 * The single implementation of local-day differencing: counting in days rather
 * than in milliseconds is what makes it exact across a DST transition, where a
 * day is 23 or 25 hours. Both the period day-index and the grain module's
 * elapsed-day count route through here (C-6).
 */
export function calendarDaysBetween(from: Date, to: Date, timeZone: string): number {
  const a = localYMD(from, timeZone);
  const b = localYMD(to, timeZone);
  const fromUtcDay = Date.UTC(a.year, a.month - 1, a.day);
  const toUtcDay = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((toUtcDay - fromUtcDay) / MS_PER_DAY);
}

/**
 * Zero-based day index of `date` within `period`, in local days.
 * Used to compare like-for-like against the previous period (SPEC.md §8).
 */
export function dayIndexInPeriod(date: Date, period: Period, timeZone: string): number {
  return calendarDaysBetween(period.start, date, timeZone);
}

/** Whole days left, counting the current day as remaining. Never negative. */
export function daysRemaining(period: Period, now: Date, timeZone: string): number {
  const elapsed = dayIndexInPeriod(now, period, timeZone);
  return Math.max(0, periodLengthDays(period) - elapsed);
}

/**
 * Human label.
 *
 * A cut-off of the 1st is just a month name — that is what the period *is*, and
 * rendering it as a range would be pointlessly technical. Any other cut-off gets
 * an explicit range, because "July" would be a lie for 25 Jun – 24 Jul.
 */
export function periodLabel(
  period: Period,
  cycleStartDay: number,
  timeZone: string,
  locale = "en-SG",
): string {
  const start = localYMD(period.start, timeZone);

  if (cycleStartDay === 1) {
    return labelFormatter(`month:${locale}:${timeZone}`, () =>
      new Intl.DateTimeFormat(locale, { timeZone, month: "long", year: "numeric" }),
    ).format(period.start);
  }

  const lastDay = new Date(period.end.getTime() - MS_PER_DAY);
  const fmt = labelFormatter(
    `range:${locale}:${timeZone}`,
    () => new Intl.DateTimeFormat(locale, { timeZone, day: "numeric", month: "short" }),
  );
  const endYear = localYMD(lastDay, timeZone).year;
  const yearSuffix = start.year === endYear ? "" : ` ${String(endYear)}`;
  return `${fmt.format(period.start)} – ${fmt.format(lastDay)}${yearSuffix}`;
}

/** What a row says when its stored instant cannot be read at all (INV-7). */
const UNREADABLE = "date unreadable";

/**
 * When an expense happened, in the user's zone — `28 Jul, 7:42 pm`.
 *
 * This is the label under every row in the two lists that show expenses: the
 * Ledger and the Trends category drill-down. It lives here rather than in either
 * of them because it was written twice, verbatim, and because the formatters it
 * needs belong in `labelFormatter`'s cache — the Ledger renders 200 rows at a
 * time and an `Intl.DateTimeFormat` costs ~0.1ms to build.
 *
 * **The time of day is real data, not decoration.** `addExpense` stamps the
 * exact instant of the Save tap and the edit sheet preserves it across a date
 * correction, so this states something the ledger has always known and never
 * said.
 *
 * Three kinds of row carry a *synthesised* time instead, and this renders what
 * is stored rather than hiding it: suppressing the time on them would make a row
 * that never had one indistinguishable from one genuinely logged at midday. A
 * repaired unreadable date starts at local midnight (`zonedMidnight`), so it
 * reads `12:00 am`. A recurring confirmation (`recurring.ts`) and an agent import
 * (`sounding_view.tsx`) know only a calendar day and aim for noon — but both
 * build it as `new Date(\`${ymd}T12:00:00\`)`, which parses in the **device's**
 * zone rather than the profile's, so they read noon only while the two agree.
 * They are not `zonedMidnight`'s callers and that is the bug: see ROADMAP
 * Phase 19, "Known, not fixed". Nothing here can correct it — the wrong instant
 * is already stored — and this function must not pretend otherwise.
 *
 * `withDate: false` drops the calendar date for a list already banded by day,
 * where the band header above the row has said it (DESIGN_SYSTEM §4.3).
 *
 * The locale is fixed rather than the device's, matching `periodLabel`'s default
 * and the two helpers this replaces: a row's date must not change shape with the
 * browser's language while the band header above it does not.
 */
export function occurredLabel(iso: string, timeZone: string, withDate = true, locale = "en-SG"): string {
  const at = new Date(iso);
  // `useExpensesInPeriod` bounds a LEXICAL range on the stored string, so a row
  // whose `occurredAt` is not a date can still be listed. Saying so points at
  // the edit sheet, which is the only place it can be repaired; formatting it
  // regardless throws a RangeError mid-render and blanks the app (INV-7, C-8).
  if (!Number.isFinite(at.getTime())) return UNREADABLE;

  const time = labelFormatter(
    `row-time:${locale}:${timeZone}`,
    () => new Intl.DateTimeFormat(locale, { timeZone, hour: "numeric", minute: "2-digit" }),
  ).format(at);
  if (!withDate) return time;

  const date = labelFormatter(
    `row-date:${locale}:${timeZone}`,
    () => new Intl.DateTimeFormat(locale, { timeZone, day: "numeric", month: "short" }),
  ).format(at);
  return `${date}, ${time}`;
}
